import DeleteAiModel from '@Application/Ai/AiConfiguration/Models/Commands/DeleteAiModel';
import deleteAiModel from '@Application/Ai/AiConfiguration/Models/deleteAiModel';
import DeleteAiProvider from '@Application/Ai/AiConfiguration/Providers/Commands/DeleteAiProvider';
import deleteAiProvider from '@Application/Ai/AiConfiguration/Providers/deleteAiProvider';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import modelToDeleteSignal, {
	setModelToDelete,
} from '@UI/Pages/Administration/AiEngines/Signals/modelToDeleteSignal';
import providerToDeleteSignal, {
	setProviderToDelete,
} from '@UI/Pages/Administration/AiEngines/Signals/providerToDeleteSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { ConfirmDeleteDialog } from '@verclam/ui-kit';

/**
 * The two hard-deletion dialogs (provider, model), mounted unconditionally alongside the screen's
 * content. Self-contained — reads the provider and model catalogues already fetched by
 * `AiEnginesContent` through plain `useSelector`s (never a second `useStoreQuery`, which would fire a
 * second network call), and the two pending-deletion signals, rather than through props.
 *
 * A third, reversible confirmation exists in this feature — "revenir à l'hérité" for an assignment
 * override — but it needs neither a catalogue lookup nor cross-tree state: its context (one usage, one
 * scope) is already local to the card that triggers it, so it is rendered there
 * (`UsageAssignmentCardActions`) rather than centralized here.
 */
const AiEnginesConfirmationDialogs: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;

	const providers = useSelector((state) => state.aiConfigurationStore.providers);
	const models = useSelector((state) => state.aiConfigurationStore.models);

	const providerToDeleteId = useSignalValue(providerToDeleteSignal);
	const modelToDeleteId = useSignalValue(modelToDeleteSignal);

	const providerBeingDeleted = React.useMemo(
		() => (providerToDeleteId !== null ? (providers?.findById(providerToDeleteId) ?? null) : null),
		[providers, providerToDeleteId],
	);

	const modelBeingDeleted = React.useMemo(
		() => (modelToDeleteId !== null ? (models?.findById(modelToDeleteId) ?? null) : null),
		[models, modelToDeleteId],
	);

	const handleProviderDeleteHide = React.useCallback(() => {
		setProviderToDelete(null);
	}, []);

	const handleProviderDeleteAccept = React.useCallback(() => {
		if (accountId === null || providerToDeleteId === null) {
			return;
		}

		dispatch(deleteAiProvider(new DeleteAiProvider(accountId, providerToDeleteId))).catch(
			ignoreHandledError,
		);
		// Close immediately: `acceptCallback` never triggers `onHide`, and waiting for the request to
		// settle would leave the dialog open long enough for a second click.
		setProviderToDelete(null);
	}, [accountId, providerToDeleteId, dispatch]);

	const handleModelDeleteHide = React.useCallback(() => {
		setModelToDelete(null);
	}, []);

	const handleModelDeleteAccept = React.useCallback(() => {
		if (accountId === null || modelToDeleteId === null) {
			return;
		}

		dispatch(deleteAiModel(new DeleteAiModel(accountId, modelToDeleteId))).catch(ignoreHandledError);
		setModelToDelete(null);
	}, [accountId, modelToDeleteId, dispatch]);

	return (
		<React.Fragment>
			<ConfirmDeleteDialog
				visible={providerToDeleteId !== null}
				onHide={handleProviderDeleteHide}
				header="Supprimer le fournisseur"
				message={
					<React.Fragment>
						Cette action est irréversible. Confirmer la suppression du fournisseur{' '}
						<strong>{providerBeingDeleted?.label ?? ''}</strong> ?
					</React.Fragment>
				}
				confirmText={providerBeingDeleted?.label ?? ''}
				acceptCallback={handleProviderDeleteAccept}
			/>
			<ConfirmDeleteDialog
				visible={modelToDeleteId !== null}
				onHide={handleModelDeleteHide}
				header="Supprimer le modèle"
				message={
					<React.Fragment>
						Cette action est irréversible. Confirmer la suppression du modèle{' '}
						<strong>{modelBeingDeleted?.label ?? ''}</strong> ?
					</React.Fragment>
				}
				confirmText={modelBeingDeleted?.label ?? ''}
				acceptCallback={handleModelDeleteAccept}
			/>
		</React.Fragment>
	);
};

export default AiEnginesConfirmationDialogs;
